import type { AxiosInstance } from '@glovo/fetcher'
import { consoleLog } from '../console-log'
import { togglesDictionary } from './dictionary'
import type { BackendToggle, ToggleName, TogglesDictionary, Writable } from './types'
import { getConfig } from '@/tools/config'

export const fetchToggles = async (
  fetcher: AxiosInstance,
  onError: (error: unknown) => void
): Promise<TogglesDictionary> => {
  const { apiUrl } = getConfig()
  const toggles = Object.keys(togglesDictionary)
  try {
    const response = await fetcher.post(`${apiUrl}/features/query`, { toggles })
    const normalizedBackendToggles = normalizeBackendToggles(response.data.toggles)
    const result = completeCollectionWithDefaultValues(normalizedBackendToggles, togglesDictionary)

    consoleLog.pink('Toggles', 'Fetching toggles', {
      withUserId: Boolean(response.config?.headers?.Authorization),
      cityCode: response.config?.headers['Glovo-Location-City-Code'],
      countryCode: response.config?.headers['Glovo-Location-Country-Code'],
    })

    return result
    // eslint-disable-next-line @glovo/custom-rules/always-forward-error
  } catch (error) {
    if (onError) {
      onError(error)
    }
    return togglesDictionary
  }
}

/**
 *
 * Convert the backend toggles collection object into a key-value dictionary
 *
 */
const normalizeBackendToggles = (
  backendToggles?: Array<BackendToggle>
): Partial<TogglesDictionary> => {
  const normalizedBackendToggles: Partial<TogglesDictionary> = backendToggles
    ? backendToggles.reduce((acc: Writable<Partial<TogglesDictionary>>, item: BackendToggle) => {
        acc[item.name] = item.value
        return acc
      }, {})
    : {}
  return normalizedBackendToggles
}

/**
 *
 * Create a toggles dictionary where each toggle is either the value from the backend or the
 * default value from the declaration dictionary.
 *
 */
const completeCollectionWithDefaultValues = (
  backendToggles: Partial<TogglesDictionary>,
  toggleDefaultValues: TogglesDictionary
): TogglesDictionary => {
  const result: Record<PropertyKey, unknown> = {}
  for (const name of Object.keys(togglesDictionary)) {
    const toggleName = name as ToggleName
    if (toggleName in toggleDefaultValues) {
      result[toggleName] = backendToggles[toggleName] ?? togglesDictionary[toggleName]
    }
  }
  return result as TogglesDictionary
}
